'use client'

/**
 * MobileStickyBuyBar — the phone-width persistent buy bar on the product page
 * (extracted from product-client.tsx on 09/16/2026, FRIDGE-908, unchanged
 * markup). The single biggest mobile-conversion lever: price, retailer and
 * the signed "View Deal" /r/ redirect (pos=sticky for per-module EPC
 * attribution — the desktop bar is pos=sticky-desktop, PdpStickyBar).
 *
 * When it shows is decided by the caller through lib/hero-boundary.ts:
 * after the hero has scrolled past, and — FRIDGE-908 — on phones also while
 * the buy box is still below the first screen, so a visitor an assistant sent
 * here has the price and the deal button in reach without scrolling.
 *
 * lg:hidden so it never stacks with desktop layouts; z-40 below ComparisonBar
 * (z-50) so an active comparison selection wins the slot. Renders nothing
 * without a fresh-priced offer (the caller gates on it too).
 */

import { LearnAboutRecallLink, RetailerOfferCta } from '@/components/product/retailer-offer-cta'
import { SaveHeartButton, type SavedItem } from '@/components/chrome/save-compare-controls'

export interface MobileStickyBuyBarProps {
  offer: { id: string; name: string; priceDisplay: string } | null
  productId: string
  savedItem: SavedItem
  hasActiveRecall: boolean
}

export function MobileStickyBuyBar({ offer, productId, savedItem, hasActiveRecall }: MobileStickyBuyBarProps) {
  if (!offer?.id) return null
  return (
    <div
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-4 py-2.5 flex items-center justify-between gap-3"
      data-testid="sticky-buy-bar"
    >
      <div className="min-w-0">
        <p className="text-[11px] text-gray-500 truncate">{offer.name}</p>
        <p className="text-base font-black text-gray-900">{offer.priceDisplay}</p>
      </div>
      <SaveHeartButton item={savedItem} />
      {hasActiveRecall && <LearnAboutRecallLink />}
      <RetailerOfferCta
        offerId={offer.id}
        productId={productId}
        pos="sticky"
        target="_blank"
        aria-label={`View deal at ${offer.name}, ${offer.priceDisplay} (opens retailer site in new tab)`}
        hasActiveRecall={hasActiveRecall}
        size="sm"
      >
        View Deal
      </RetailerOfferCta>
    </div>
  )
}
